import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import QRCode from 'qrcode.react';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import LoadingSpinner from '../../components/LoadingSpinner';
import {
  AdsQueue,
  delay,
  getDeviceAds,
  getFallbackAd,
  IAdLoop,
  QueueConfig,
} from './requests';
import { cleanCurrentAd, parseConfig } from '../../utils/helpers';

import {
  axiosConfig, cdnURL, handleQueues, queues,
} from '../../config';

import {
  Actions,
  StrapiComponentType,
  useAdLoopReducer,
} from './useAdLoopReducer';
import AdPlayer from './AdPlayer';
import { AdType, Device } from '../../types';
import { useComponentRef } from './useComponentRef';

const FOOTER_HEIGHT = 10; // percent
const FOOTER_QR_URL = 'https://portlmedia.com';

// Original full-screen layout (all devices without a footer)
const AdContainer = styled.div`
    width: 100%;
    height: initial;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    overflow: hidden;
    background-color: black;
`;

// Footer layout — same absolute positioning as AdContainer so the video's
// height: 100% resolves against the .app div, not an intermediate wrapper.
const AdContainerWithFooter = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: ${100 - FOOTER_HEIGHT}%;
    overflow: hidden;
    background-color: black;
    display: flex;
    align-items: center;

    > * {
        width: 100%;
    }
`;

const Footer = styled.div`
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: ${FOOTER_HEIGHT}%;
    background-color: black;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
    padding: 0 24px;
    box-sizing: border-box;
`;

const FooterText = styled.span`
    color: white;
    background: transparent;
    font-size: 1.2rem;
    font-weight: 500;
`;

const FooterBar = () => (
  <Footer>
    <FooterText>Scan to learn more</FooterText>
    <QRCode value={FOOTER_QR_URL} size={64} bgColor='#000000' fgColor='#ffffff' />
  </Footer>
);

const BrandedImage = styled.img`
    height: 101%;
    width: 101%;
  `;

type AdLoopProps = {
  device?: Device
};

export const DEFAULT_CONFIG = {
  queueCap: { ads: 5 },
  clientAdRequestIntervals: {
    ms: 2 * 1000,
  },
  weights: {
    server: 8,
    fallback: 1,
    component: 1,
    weather: 1,
    news: 1,
  },
} as Record<string, any>;

const preloadMedia = (ad: IAdLoop): void => {
  if (!ad?.mediaFile?.blobURL) return;
  // eslint-disable-next-line no-underscore-dangle
  if (ad.mediaFile._type?.includes('image')) {
    const img = new Image();
    img.src = ad.mediaFile.blobURL;
  }
  // Video preloading intentionally omitted — GeckoView buffers the full video
  // into RAM for each preload hint, exhausting memory on TV hardware.
};

const DEFAULT_FALLBACK: IAdLoop = {
  duration: 6,
  uniqueId: uuidv4(),
  createdAt: Date.now(),
  mediaFile: {
    blobURL: '../assets/black.png',
    _type: 'image/png',
  },
  pop: undefined,
};

const AdLoop = ({ device }: AdLoopProps): React.ReactElement => {
  const [state, dispatch] = useAdLoopReducer();
  const { organization } = device || {};

  const componentRef = useComponentRef();

  const config = useRef<QueueConfig>(DEFAULT_CONFIG as QueueConfig);
  const controllerRef = useRef<AbortController>(new AbortController());
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // const isPopProcessingRef = useRef(false);

  const triggerPopFlow = async ({
    ad,
    onPopInit = () => {},
    onPopSuccess = () => {},
    onPopError = () => {},
    onPopFinally = () => {},
  }: {
    ad: IAdLoop;
    onPopInit?: () => void;
    onPopSuccess?: () => void;
    onPopError?: () => void;
    onPopFinally?: () => void;
  }) => {
    // console.log(`[CLIENT] Triggering POP for ad ${ad.adId || ad.uniqueId}, URL: ${ad.pop}`);
    try {
      onPopInit();
      const popUrl = ad.pop;
      if (!popUrl) {
        console.log('Pop URL is undefined');
        return;
      }
      await axios.get(popUrl, axiosConfig);
      // console.log(`[CLIENT] POP successful for ad ${ad.adId || ad.uniqueId}`);
      onPopSuccess();
    } catch (error) {
      console.error(`[CLIENT] POP failed for ad ${ad.adId || ad.uniqueId}:`, error);
      onPopError();
    } finally {
      onPopFinally();
    }
  };

  const popAds = async () => {
    // if (isPopProcessingRef.current) return;
    if (!queues.current || !queues.current.serverQueue.length) return;

    // isPopProcessingRef.current = true;

    try {
      if (!queues.current.poppedAdsQueue) {
        queues.current.poppedAdsQueue = [];
      }

      while (queues.current.serverQueue.length > 0) {
        const ad = queues.current?.serverQueue[0];

        onUpdateQueue(ad, 'serverQueue');

        if (!ad?.pop) {
          queues.current?.poppedAdsQueue.push(ad);
          preloadMedia(ad);
          // eslint-disable-next-line no-continue
          continue;
        }

        const onPopSuccess = () => {
          queues.current?.poppedAdsQueue.push(ad);
          preloadMedia(ad);
        };

        await triggerPopFlow({ ad, onPopSuccess });
      }
    } finally {
      console.log('finally');
    }
    // finally {
    //   isPopProcessingRef.current = false;
    // }
  };

  const fallbackAd = useRef<IAdLoop>(DEFAULT_FALLBACK);
  const consecutiveErrorsRef = useRef(0);

  const getNext = () => {
    if (!queues.current) {
      return;
    }

    popAds();

    const {
      poppedAdsQueue,
    } = queues.current;

    const adArray: Actions[] = [];
    const pushToAdArray = (count: number, action: Actions) => {
      for (let i = 0; i < count; i += 1) {
        adArray.push(action);
      }
    };

    if (poppedAdsQueue?.length > 0) {
      pushToAdArray(config.current.weights?.[AdType.Server], { type: 'SET_AD', payload: poppedAdsQueue[0] });
    }

    // news and weather logic
    if (!componentRef.current?.news.error) {
      pushToAdArray(config.current.weights?.[AdType.News], { type: 'SET_COMPONENT', payload: StrapiComponentType.News });
    }

    if (!componentRef.current?.weather.error) {
      pushToAdArray(config.current.weights?.[AdType.Weather], { type: 'SET_COMPONENT', payload: StrapiComponentType.Weather });
    }

    if (fallbackAd.current) {
      pushToAdArray(config.current.weights?.[AdType.Fallback], { type: 'SET_AD', payload: { ...fallbackAd.current, uniqueId: uuidv4() } });
    }

    // No content ready yet — retry in 1s to hold the last frame instead of showing fallback
    if (adArray.length === 0) {
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
      retryTimerRef.current = setTimeout(getNext, 1000);
      return;
    }

    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }

    const weightedDispatchIndex = Math.floor(Math.random() * adArray.length);
    dispatch(adArray[weightedDispatchIndex]);
  };

  const getDeviceAd = async () => {
    if ((queues.current?.poppedAdsQueue?.length || 0) >= config.current.queueCap.ads) { return; }

    try {
      const ads = await getDeviceAds({ partnerName: organization });

      if (ads.length === 0 || !queues.current) return;

      ads.forEach((ad) => {
        queues.current?.serverQueue.push(ad);
      });
    } catch (error) {
      console.error('Failed to fetch ads:', error);
    }
  };

  // Signal is passed by value so each loop instance holds a reference to its own
  // AbortSignal — replacing controllerRef.current won't accidentally keep an old loop alive.
  const startProviderLoop = async (signal: AbortSignal) => {
    while (!signal.aborted) {
      // console.log(`[CLIENT] Making ad request at ${new Date().toISOString()}`);
      await getDeviceAd();
      const delayMs = config.current.clientAdRequestIntervals.ms || 20 * 1000;
      // console.log(`[CLIENT] Waiting ${delayMs}ms before next request`);
      await delay(delayMs);
    }
  };

  const fetchInitQueues = async () => {
    // Stop any running provider loop before starting a new one
    controllerRef.current.abort();
    const controller = new AbortController();
    controllerRef.current = controller;

    dispatch({ type: 'SET_LOADING' });

    // setting up config
    await parseConfig(config);
    const fallbackAdResponse = await getFallbackAd({ partnerName: organization });
    if (fallbackAdResponse) {
      fallbackAd.current = fallbackAdResponse;
    }

    handleQueues({
      serverQueue: [],
      poppedAdsQueue: [],
      weatherQueue: [],
      newsQueue: [],
    });

    // Pre-fetch the first ad so getNext() doesn't immediately fall back to
    // the portl placeholder image while the provider loop hasn't run yet.
    await getDeviceAd();

    dispatch({ type: 'TOGGLE_LOADING', payload: false });
    startProviderLoop(controller.signal);
    getNext();
  };

  const onUpdateQueue = (ad: IAdLoop, queueKey: keyof AdsQueue) => {
    if (queues.current && ad) {
      (queues.current[queueKey] as IAdLoop[]) = cleanCurrentAd(ad, queues.current?.[queueKey] as IAdLoop[]);
    }
  };

  const onAdEnded = () => {
    consecutiveErrorsRef.current = 0;
    onUpdateQueue(state.ad, 'poppedAdsQueue');
    getNext();
  };

  const onAdError = () => {
    if (state.error || state.loading || !queues.current) {
      return;
    }

    consecutiveErrorsRef.current += 1;
    if (consecutiveErrorsRef.current >= 3) {
      // 3+ consecutive media errors = decoder is in a fatal state; reload to reset it
      window.location.reload();
      return;
    }

    onUpdateQueue(state.ad, 'poppedAdsQueue');
    getNext();
  };

  useEffect(() => () => {
    controllerRef.current.abort();
    if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
  }, []);

  useEffect(() => {
    if (!device) return;
    fetchInitQueues();
    // fetchInitQueues depends on stable refs/functions (config, componentRef, dispatch, handleQueues, getNext)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [device]);

  useEffect(() => {
    if (!state.error) {
      return undefined;
    }

    console.log('Resetting ad loop...');
    const timerId = setTimeout(() => {
      fetchInitQueues();
    }, 20 * 1000);

    return () => {
      clearTimeout(timerId);
    };
    // fetchInitQueues is stable, only state.error should trigger this
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.error]);

  const hasFooter = organization === 'irina';

  const renderContent = () => {
    if (state.loading) return <LoadingSpinner />;
    if (state.error) return <BrandedImage src={`${cdnURL}/external/fallbacks/432x432.png`} alt='Error' />;
    return (
      <AdPlayer
        mediaFile={state.ad?.mediaFile}
        duration={state.ad?.duration}
        onEnded={onAdEnded}
        onError={onAdError}
        adKey={state.ad?.uniqueId}
        componentProps={{ componentType: state.component, component: componentRef.current[state.component] }}
        // objectFit={hasFooter ? 'cover' : 'contain'}
      />
    );
  };

  if (hasFooter) {
    return (
      <>
        <AdContainerWithFooter>
          {renderContent()}
        </AdContainerWithFooter>
        <FooterBar />
      </>
    );
  }

  return <AdContainer>{renderContent()}</AdContainer>;
};

export default AdLoop;
